import { Button, Input, notification, Table, Tag } from 'antd'
import React, { useMemo, useState } from 'react'
import {
  useCreateUserMutation,
  useFetchModulesListQuery,
  useFetchUserListQuery,
  useLazyFetchUserByIdQuery,
  useResetUserPasswordMutation,
  useUpdateUserMutation,
} from 'domain/User/userApi'
import Card from 'components/Card'
import CreateUserDrawer from './CreateUserDrawer'
import Heading from 'components/Heading'
import { useTranslation } from 'react-i18next'

import './userManagement.less'
import '../../global/styles/global.less'

const { Search } = Input

const normalizeUserResponse = user => ({
  email: user.email || null,
  id: user.id,
  laboratory: user.laboratoryId || null,
  module: Array.isArray(user.moduleIds) ? user.moduleIds : [],
  role: user.role?.toLowerCase?.() ?? user.role,
  status: user.isActive ? 'active' : 'inactive',
  surveyCompany: user.surveyCompanyId || null,
  username: user.username || user.email,
  vessel: user.imoNumber ?? user.imonumber ?? null,
  vesselList: Array.isArray(user.imoNumberList)
    ? user.imoNumberList
    : Array.isArray(user.imonumberlist)
      ? user.imonumberlist
      : [],
})

const UserManagement = () => {
  const { t } = useTranslation()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [submitError, setSubmitError] = useState('')
  const [searchValue, setSearchValue] = useState('')
  const { data: userList = [], isLoading } = useFetchUserListQuery()
  const { data: modulesList = [] } = useFetchModulesListQuery()
  const [fetchUserById, { isFetching: isFetchingUserById }] =
    useLazyFetchUserByIdQuery()
  const [createUser, { isLoading: isCreatingUser }] = useCreateUserMutation()
  const [updateUser, { isLoading: isUpdatingUser }] = useUpdateUserMutation()
  const [resetUserPassword, { isLoading: isResettingPassword }] =
    useResetUserPasswordMutation()

  const moduleNameById = useMemo(
    () =>
      (Array.isArray(modulesList) ? modulesList : []).reduce((acc, module) => {
        acc[module.id] = module.name

        return acc
      }, {}),
    [modulesList]
  )

  const normalizedUsers = useMemo(
    () => (Array.isArray(userList) ? userList : []).map(normalizeUserResponse),
    [userList]
  )

  const filteredUsers = useMemo(
    () =>
      normalizedUsers.filter(user =>
        user.username.toLowerCase().includes(searchValue.toLowerCase())
      ),
    [normalizedUsers, searchValue]
  )

  const handleEditClick = async record => {
    setSubmitError('')
    setSelectedUser(null)
    setIsDrawerOpen(true)

    try {
      const userDetailsResponse = await fetchUserById(record.id).unwrap()
      const userDetails = userDetailsResponse?.data ?? userDetailsResponse

      setSelectedUser(normalizeUserResponse({ ...record, ...userDetails }))
    } catch {
      setSelectedUser(record)
    }
  }

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false)
    setSelectedUser(null)
    setSubmitError('')
  }

  const handleSubmitUser = async values => {
    if (selectedUser) {
      try {
        const payload = {
          id: selectedUser.id,
          imoNumber: values.vessel || null,
          imoNumberList: values.vesselList || null,
          isActive: values.status === 'active',
          laboratoryId: values.laboratory || null,
          moduleIds: values.module || [],
          role: values.role?.toUpperCase?.() ?? values.role,
          surveyCompanyId: values.surveyCompany || null,
        }

        const result = await updateUser(payload).unwrap()

        if (result?.code && result.code !== '200') {
          setSubmitError(result.message || 'An unexpected error occurred')

          return
        }

        handleCloseDrawer()
      } catch (error) {
        const message =
          error?.data?.message ||
          error?.message ||
          'An unexpected error occurred'

        setSubmitError(message)
      }

      return
    }

    try {
      const payload = {
        email: values.email,
        imoNumber: values.vessel || null,
        imoNumberList: values.vesselList || null,
        isActive: values.status === 'active',
        laboratoryId: values.laboratory || null,
        moduleIds: values.module || [],
        role: values.role?.toUpperCase?.() ?? values.role,
        surveyCompanyId: values.surveyCompany || null,
        username: values.username,
      }

      const result = await createUser(payload).unwrap()

      if (result?.code && result.code !== '200') {
        setSubmitError(result.message || 'An unexpected error occurred')

        return
      }

      handleCloseDrawer()
    } catch (error) {
      const message =
        error?.data?.message || error?.message || 'An unexpected error occurred'

      setSubmitError(message)
    }
  }

  const handleResetPassword = async () => {
    if (!selectedUser?.id) {
      return
    }

    setSubmitError('')

    try {
      const result = await resetUserPassword(selectedUser.id).unwrap()

      if (result?.code === '200') {
        notification.success({
          message: result?.message || 'Password reset successfully',
        })

        return
      }

      setSubmitError(result?.message || 'An unexpected error occurred')
    } catch (error) {
      const message =
        error?.data?.message || error?.message || 'An unexpected error occurred'

      setSubmitError(message)
    }
  }

  const columns = [
    {
      dataIndex: 'username',
      key: 'username',
      sorter: (a, b) => a.username.localeCompare(b.username),
      title: t('admin.userManagement.table.username'),
    },
    {
      dataIndex: 'role',
      key: 'role',
      title: t('admin.userManagement.table.role'),
    },
    {
      dataIndex: 'module',
      key: 'module',
      render: value => {
        if (Array.isArray(value)) {
          return value
            .map(moduleValue => moduleNameById[moduleValue] || moduleValue)
            .join(', ')
        }

        return moduleNameById[value] || value
      },
      title: t('admin.userManagement.table.module'),
    },
    {
      dataIndex: 'status',
      key: 'status',
      render: value => (
        <Tag color={value === 'active' ? 'green' : 'red'}>
          {value === 'active'
            ? t('admin.userManagement.table.active')
            : t('admin.userManagement.table.inactive')}
        </Tag>
      ),
      title: t('admin.userManagement.table.status'),
    },
    {
      key: 'action',
      render: (_, record) => (
        <Button
          onClick={() => handleEditClick(record)}
          size="small"
          type="link"
        >
          {t('admin.userManagement.table.edit')}
        </Button>
      ),
      title: t('admin.userManagement.table.action'),
    },
  ]

  return (
    <div className="page__container">
      <Heading level={1}>{t('admin.userManagement.title')}</Heading>
      <Card
        className="user-management__card"
        title={t('admin.userManagement.cardTitle')}
        headerActionButton={{
          label: t('admin.userManagement.create'),
          onClick: () => setIsDrawerOpen(true),
        }}
      >
        <Search
          allowClear
          enterButton
          onSearch={value => setSearchValue(value)}
          placeholder={t('admin.userManagement.searchPlaceholder')}
          style={{ marginBottom: 16, maxWidth: 400 }}
          value={searchValue}
          onChange={e => {
            if (!e.target.value) {
              setSearchValue('')
            } else {
              setSearchValue(e.target.value)
            }
          }}
        />
        <Table
          columns={columns}
          dataSource={filteredUsers}
          loading={isLoading}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          rowKey="id"
        />
      </Card>
      <CreateUserDrawer
        editData={selectedUser}
        loading={isFetchingUserById}
        onClose={handleCloseDrawer}
        onResetPassword={handleResetPassword}
        onSubmit={handleSubmitUser}
        open={isDrawerOpen}
        resettingPassword={isResettingPassword}
        submitError={submitError}
        submitting={isCreatingUser || isUpdatingUser}
      />
    </div>
  )
}

export default UserManagement
